import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { axiosInstance } from "../../api/api";
import { Routes } from "../../routes";

interface Patrol {
  id: number;
  name: string;
}

export const CreateGroup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const groupId = location.state?.groupId || null;

  const [groupName, setGroupName] = useState("");
  const [selectedPatrols, setSelectedPatrols] = useState<number[]>([]);
  const [patrols, setPatrols] = useState<Patrol[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPatrols = async () => {
    try {
      setFetchLoading(true);
      const res = await axiosInstance.get("/api/v1/patrol/index", {
        params: { per_page: 50 },
      });
      setPatrols(res.data.data || []);
    } catch (err: any) {
      setError("Gagal memuat data patrol");
      console.error("❌ Error fetching patrols:", err);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchGroupDetails = async (groupId: number) => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/patrol/group/show/${groupId}`
      );

      console.log("📦 GROUP DETAILS RESPONSE:", res.data);

      const groupData = res.data?.data || res.data;

      if (!groupData) {
        console.error("❌ Group data not found in API response");
        return;
      }

      setGroupName(groupData.name || "");
      setSelectedPatrols(
        Array.isArray(groupData.patrols)
          ? groupData.patrols.map((p: any) => p.id)
          : []
      );

      console.log("✅ Loaded group details:", {
        name: groupData.name,
        patrolCount: groupData.patrols?.length,
      });
    } catch (err) {
      console.error("❌ Error fetching group details:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchPatrols();
      if (groupId) {
        await fetchGroupDetails(groupId);
      }
    };
    init();
  }, [groupId]);

  const handlePatrolSelect = (patrolId: number) => {
    setSelectedPatrols((prev) =>
      prev.includes(patrolId)
        ? prev.filter((id) => id !== patrolId)
        : [...prev, patrolId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPatrols.length === patrols.length) {
      setSelectedPatrols([]);
    } else {
      setSelectedPatrols(patrols.map((p) => p.id));
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!groupName.trim()) {
      setError("Nama group harus diisi");
      return;
    }

    if (selectedPatrols.length === 0) {
      setError("Pilih minimal 1 patrol");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        name: groupName.trim(),
        patrols: selectedPatrols.map((id) => ({ id })),
      };

      let response;

      if (groupId) {
        console.log("✏️ UPDATE GROUP PAYLOAD:", payload);
        response = await axiosInstance.post(
          `/api/v1/patrol/group/update/${groupId}`,
          payload
        );
      } else {
        console.log("🆕 CREATE GROUP PAYLOAD:", payload);
        response = await axiosInstance.post(
          "/api/v1/patrol/group/store",
          payload
        );
      }

      console.log("✅ GROUP RESPONSE:", response.data);

      const data = response.data?.data || response.data;
      const newGroupId = data?.id || groupId;

      if (data) {
        navigate(`${Routes.AssignUsersToGroup}/${newGroupId}`, {
          state: {
            groupName: groupName.trim(),
            patrolCount: selectedPatrols.length,
            isEdit: !!groupId,
          },
        });
      }
    } catch (err: any) {
      console.error("❌ GROUP SUBMIT ERROR:", err);
      console.error("Error response:", err.response?.data);
      setError(err.response?.data?.message || "Gagal menyimpan group");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <div>Loading patrols...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }}>
      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          border: "1px solid #ddd",
        }}
      >
        <h1 style={{ textAlign: "center", marginBottom: "20px" }}>
          {groupId ? "Edit Group" : "Buat Group Baru"}
        </h1>

        {error && (
          <div
            style={{
              backgroundColor: "#ffebee",
              border: "1px solid #f44336",
              color: "#c62828",
              padding: "12px",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Input Nama Group */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "bold",
              }}
            >
              Nama Group *
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "16px",
              }}
              placeholder="Masukkan nama group"
              required
            />
          </div>

          {/* Patrol Selection */}
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "4px",
              padding: "16px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h2 style={{ margin: 0 }}>
                Pilih Patrol ({selectedPatrols.length} terpilih)
              </h2>
              <button
                type="button"
                onClick={handleSelectAll}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              >
                {selectedPatrols.length === patrols.length
                  ? "Batal Pilih Semua"
                  : "Pilih Semua"}
              </button>
            </div>

            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {patrols.length === 0 ? (
                <p
                  style={{
                    textAlign: "center",
                    color: "#666",
                    padding: "20px",
                  }}
                >
                  Tidak ada data patrol
                </p>
              ) : (
                patrols.map((patrol) => (
                  <div
                    key={patrol.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`patrol-${patrol.id}`}
                      checked={selectedPatrols.includes(patrol.id)}
                      onChange={() => handlePatrolSelect(patrol.id)}
                      style={{ marginRight: "8px" }}
                    />
                    <label htmlFor={`patrol-${patrol.id}`}>
                      {patrol.name}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "24px",
            }}
          >
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={loading}
              style={{
                padding: "10px 20px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "white",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={
                loading || !groupName.trim() || selectedPatrols.length === 0
              }
              style={{
                padding: "10px 20px",
                backgroundColor:
                  loading ||
                  !groupName.trim() ||
                  selectedPatrols.length === 0
                    ? "#ccc"
                    : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor:
                  loading ||
                  !groupName.trim() ||
                  selectedPatrols.length === 0
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {loading
                ? groupId
                  ? "Menyimpan..."
                  : "Membuat..."
                : groupId
                ? "Simpan Perubahan"
                : "Buat Group & Lanjut Assign Users"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
